import React, { useState } from "react";
import { X, Sparkles, RotateCcw, Save, Eye, MapPin, ExternalLink, Loader2 } from "lucide-react";
import { getStateUrl } from "../../constants";
import { generateDefaultStateContent } from "../../api";
import FormattedText from "../../components/FormattedText";

const StateContentForm = ({
  state,
  currentContent,
  stateCourses = [],
  onSave,
  onReset,
  onClose,
}) => {
  const defaultContent = generateDefaultStateContent(state.name, stateCourses);

  const [formData, setFormData] = useState({
    customTitle: currentContent?.customTitle || defaultContent.customTitle,
    leadParagraph: currentContent?.leadParagraph || defaultContent.leadParagraph,
    contentParagraphsText: (currentContent?.contentParagraphs || defaultContent.contentParagraphs).join("\n\n"),
  });

  const [activeTab, setActiveTab] = useState("edit"); // "edit" or "preview"
  const [isSaving, setIsSaving] = useState(false);

  const contentParagraphs = formData.contentParagraphsText
    .split(/\n\n+/)
    .map((p) => p.trim())
    .filter(Boolean);

  const courseCount = stateCourses.length;
  const citiesCount = new Set(stateCourses.map((c) => c.city).filter(Boolean)).size;
  const avgRating = courseCount > 0
    ? (stateCourses.reduce((sum, c) => sum + Number(c.rating || 0), 0) / courseCount).toFixed(1)
    : "4.5";
  const sorted = [...stateCourses].sort((a, b) => (b.rating - a.rating) || (b.reviewCount - a.reviewCount));
  const topCourse = sorted[0];

  const handleApplyDefaults = () => {
    setFormData({
      customTitle: defaultContent.customTitle,
      leadParagraph: defaultContent.leadParagraph,
      contentParagraphsText: defaultContent.contentParagraphs.join("\n\n"),
    });
  };

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (isSaving) return;
    setIsSaving(true);
    try {
      await onSave({
        stateCode: state.code,
        stateName: state.name,
        customTitle: formData.customTitle.trim(),
        leadParagraph: formData.leadParagraph.trim(),
        contentParagraphs,
      });
    } catch (err) {
      console.error("Error saving state content:", err);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 w-full max-w-4xl my-8 overflow-hidden flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center text-[#e5243b]">
              <MapPin className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                Edit State Page Content
              </h2>
              <p className="text-xs text-gray-500">
                {state.name} ({state.code.toUpperCase()}) &middot;{" "}
                <span className="font-semibold text-gray-700">{courseCount} Courses across {citiesCount} Cities</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={getStateUrl(state.code)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:text-[#e5243b] hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ExternalLink className="w-3.5 h-3.5" /> View Live Page
            </a>
            <button
              onClick={onClose}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-200/60 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Edit / Preview Tabs */}
        <div className="px-6 pt-3 border-b border-gray-200 flex gap-4 text-sm font-medium">
          <button
            type="button"
            onClick={() => setActiveTab("edit")}
            className={`pb-3 border-b-2 font-semibold transition-colors ${
              activeTab === "edit"
                ? "border-[#e5243b] text-[#e5243b]"
                : "border-transparent text-gray-500 hover:text-gray-900"
            }`}
          >
            Content Editor
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("preview")}
            className={`pb-3 border-b-2 font-semibold transition-colors flex items-center gap-1.5 ${
              activeTab === "preview"
                ? "border-[#e5243b] text-[#e5243b]"
                : "border-transparent text-gray-500 hover:text-gray-900"
            }`}
          >
            <Eye className="w-4 h-4" /> Live Preview
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto flex-1">
          {activeTab === "edit" ? (
            <form id="state-content-form" onSubmit={handleSubmit} className="space-y-5">
              {/* Custom Title */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                  Page Title / Main Heading (H1)
                </label>
                <input
                  type="text"
                  value={formData.customTitle}
                  onChange={(e) => setFormData({ ...formData, customTitle: e.target.value })}
                  placeholder={`Mini Golf Courses in ${state.name}`}
                  className="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#e5243b] font-medium"
                  required
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  The primary headline displayed in bold white text at the top of the state page.
                </p>
              </div>

              {/* Lead Paragraph */}
              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-700 mb-1.5">
                  Lead / Summary Paragraph (Paragraph 1)
                </label>
                <textarea
                  rows={3}
                  value={formData.leadParagraph}
                  onChange={(e) => setFormData({ ...formData, leadParagraph: e.target.value })}
                  placeholder={`Looking for the best mini golf courses in ${state.name}? Explore ${courseCount} locations...`}
                  className="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#e5243b] leading-relaxed"
                  required
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  High-level state introduction. Hyperlinks supported: <code className="bg-gray-100 px-1 py-0.5 rounded text-[#e5243b] font-mono">[Link Text](https://example.com)</code>
                </p>
              </div>

              {/* Content Paragraphs */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-bold uppercase tracking-wider text-gray-700">
                    Detailed Narrative & Regional Highlights (Paragraphs 2+)
                  </label>
                  <button
                    type="button"
                    onClick={handleApplyDefaults}
                    className="text-xs font-semibold text-[#e5243b] hover:text-[#cf1c31] flex items-center gap-1"
                  >
                    <Sparkles className="w-3.5 h-3.5" /> Auto-Generate Template
                  </button>
                </div>
                <textarea
                  rows={7}
                  value={formData.contentParagraphsText}
                  onChange={(e) => setFormData({ ...formData, contentParagraphsText: e.target.value })}
                  placeholder="Whether you're planning a family outing... (Separate paragraphs with a blank line)"
                  className="w-full px-3.5 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#e5243b] font-mono leading-relaxed text-gray-800 text-xs"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  Separate paragraphs with a blank line (press Enter twice). Hyperlinks supported: <code className="bg-gray-100 px-1 py-0.5 rounded text-[#e5243b] font-mono">[Link Text](https://example.com)</code>
                </p>
              </div>
            </form>
          ) : (
            /* Live Preview Box */
            <div className="bg-[#0d0e12] rounded-2xl p-6 sm:p-8 text-gray-100 border border-gray-800 shadow-inner">
              <div className="text-xs text-gray-400 mb-4 flex items-center gap-2">
                <span>Home</span>
                <span>/</span>
                <span>States</span>
                <span>/</span>
                <span className="text-white font-medium">{state.name}</span>
              </div>

              <h1 className="text-2xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
                {formData.customTitle || `Mini Golf Courses in ${state.name}`}
              </h1>

              <div className="mt-4 bg-[#181a22] border border-gray-800 rounded-xl px-4 py-3 flex flex-wrap items-center gap-x-3 text-xs sm:text-sm text-gray-300 font-medium">
                <span className="text-white font-bold">{courseCount} Courses</span>
                <span className="text-gray-600">|</span>
                <span>{citiesCount} Cities</span>
                <span className="text-gray-600">|</span>
                <span>Average Rating: <strong className="text-white">{avgRating}</strong></span>
                <span className="text-gray-600">|</span>
                {topCourse && (
                  <span>Highest Rated: <strong className="text-white">{topCourse.name} ({topCourse.rating} stars)</strong></span>
                )}
              </div>

              <div className="mt-6 space-y-3.5 text-xs sm:text-sm leading-relaxed text-gray-300">
                <FormattedText
                  text={formData.leadParagraph}
                  paragraphClassName="text-gray-200 font-medium"
                  linkClassName="text-emerald-400 underline font-semibold hover:text-emerald-300"
                />
                {contentParagraphs.map((p, idx) => (
                  <FormattedText
                    key={idx}
                    text={p}
                    paragraphClassName="text-gray-400"
                    linkClassName="text-emerald-400 underline font-semibold hover:text-emerald-300"
                  />
                ))}
              </div>

              <div className="mt-8 pt-4 border-t border-gray-800 text-xs text-gray-400">
                <span className="text-white font-bold text-sm">{courseCount} Courses Found in {state.name}</span>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <div>
            {currentContent && (
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(`Reset custom content for ${state.name} back to auto-generated?`)) {
                    onReset(state.code);
                  }
                }}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 rounded-xl transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Revert to Auto-Generated
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200/70 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="state-content-form"
              disabled={isSaving}
              className="inline-flex items-center gap-1.5 px-5 py-2 text-sm font-bold text-white bg-[#e5243b] hover:bg-[#cf1c31] disabled:opacity-60 rounded-xl shadow-md transition-all active:scale-[0.98]"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" /> Save State Content
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StateContentForm;
